import React, { useEffect, useState } from "react";
import {
  Modal,
  Fade,
  Backdrop,
  Box,
  TextField,
  Typography,
  Stack,
  Button,
  IconButton,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";
import { postRequest } from "../../../helpers/request";
import { openPresentation } from "../../../nlpHelpers/helper";

const AssistantModal = ({ openPromptModal, setOpenPromptModal }) => {
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! How can I assist you?" },
  ]);
  const [userInput, setUserInput] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const { error, msg } = useSelector((state) => state.presentationStore);
  const navigate = useNavigate();

  useEffect(() => {
    if (error && msg) {
      setMessages((prev) => [...prev, { from: "bot", text: msg }]);
    }
  }, [error, msg]);

  const sendUserMessage = async () => {
    if (!userInput.trim()) return;

    const userMsg = userInput.trim();
    setMessages((prev) => [...prev, { from: "user", text: userMsg }]);
    setUserInput("");
    // const recentMessages = messages.slice(-6);
    const recentMessages = messages;
    const { data } = await postRequest(
      `nlp/callgemini`,
      {
        userPrompt: userMsg,
        messageHistory: recentMessages,
      },
      {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          "x-refresh-token": localStorage.getItem("refreshToken"),
          changeOrigin: true,
        },
      }
    );

    if (!data.isSuccess) return;
    // Check if it's a function call or a clarification message
    const isFunctionCall = /^\w+\((.*)\)$/.test(data.geminiResult);

    if (isFunctionCall) {
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: `Do you want me to execute: ${data.geminiResult}?`,
        },
      ]);
      setPendingAction(data.geminiResult);
    } else {
      // Gemini responded with a clarification
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: data.geminiResult },
      ]);
      setPendingAction(null);
    }
  };

  const handleConfirm = async (confirm) => {
    if (confirm && pendingAction) {
        setMessages((prev) => [
        ...prev,
        { from: "user", text: "Yes" },
        { from: "bot", text: "Done!" },
      ]);
      const { data } = await postRequest(
        `nlp/handle-ai-prompt`,
        {
          geminiResult: pendingAction,
        },
        {
          headers: {
            "x-access-token": localStorage.getItem("token"),
            "x-refresh-token": localStorage.getItem("refreshToken"),
            changeOrigin: true,
          },
        }
      );
      console.log("data", data);
      const functionMap = {
        openPresentation: (params) => openPresentation(navigate, params.id),
        // updatePresentation: (params) => updatePresentation(params.id),
        // openKeymessage: (params) => openKeymessage(params.presentationName, params.keyMessageId),
      };
      if (data.isSuccess && functionMap[data.functionName]) {
        functionMap[data.functionName](data.parameters);
      }else{
        setMessages((prev) => [
          ...prev,
          { from: "bot", text: "Sorry, I couldn’t complete the requested action. Please verify your request — the requested action might be unavailable, the item may not exist, or some required information could be missing." },
        ]);
      }
      // handleAIPrompt(pendingAction);
    
    } else {
      setMessages((prev) => [
        ...prev,
        { from: "user", text: "No" },
        { from: "bot", text: "Okay, let me know if you need anything else." },
      ]);
    }
    setPendingAction(null);
  };

  return (
    <Modal
      open={openPromptModal}
      onClose={() => {
        setOpenPromptModal(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={openPromptModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            border: "2px solid #034EA2",
            borderRadius: 4,
            boxShadow: 24,
            p: 3,
            display: "flex",
            flexDirection: "column",
            height: 600,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#034EA2", fontSize: "1.2rem", fontStyle: "Bold" }}
            >
              Assistant
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenPromptModal(false);
              }}
              size="large"
              sx={{ ml: 2 }}
            >
              ×
            </IconButton>
          </Stack>

          {/* Message history */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              mb: 2,
              px: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  alignSelf: msg.from === "user" ? "flex-end" : "flex-start",
                  bgcolor: msg.from === "user" ? "#034EA2" : "#f1f1f1",
                  color: msg.from === "user" ? "#fff" : "#000",
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  maxWidth: "80%",
                }}
              >
                {msg.text}
              </Box>
            ))}
          </Box>

          {/* Confirmation buttons */}
          {pendingAction && (
            <Stack direction="row" spacing={2} justifyContent="center" mb={1}>
              <Button variant="outlined" onClick={() => handleConfirm(true)}>
                Yes
              </Button>
              <Button variant="outlined" onClick={() => handleConfirm(false)}>
                No
              </Button>
            </Stack>
          )}

          {/* Input and send */}
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              placeholder="Type your message..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && sendUserMessage()}
            />
            <IconButton onClick={sendUserMessage} color="primary">
              <SendIcon />
            </IconButton>
          </Stack>

          {/* Suggested commands */}
          <Stack direction="row" spacing={1} mt={1}>
            {[
              "Create presentation",
              "Open presentation",
              "Open Keymessage",
            ].map((text) => (
              <Button
                variant="text"
                key={text}
                onClick={() => setUserInput(text)}
              >
                {text}
              </Button>
            ))}
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AssistantModal;
